import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useGames, useFeaturedGames, useTheme } from '@/hooks';
import { useDeviceType } from '@/utils/device';
import { MobileCarousel, MobileHistory, MobileGameGrid } from '@/components/mobile';
import { Sidebar, DesktopHeader, DesktopCarousel, DesktopGameGrid } from '@/components/desktop';
import { LoadingSpinner, ErrorState, SearchBox, MobileFooter } from '@/components/common';
import { Sun, Moon, Sparkles } from 'lucide-react';
import { useSiteSettings } from '@/contexts/settings-context';
import type { GameSummary } from '@/api/types';
import styles from './home.module.css';

export const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const deviceType = useDeviceType();
  const isMobile = deviceType === 'mobile';
  const { theme, toggleTheme } = useTheme();
  const { site, features } = useSiteSettings();
  
  const { 
    data: gamesResult, 
    isLoading: gamesLoading, 
    error: gamesError,
    refetch: refetchGames
  } = useGames();
  
  const { 
    data: featuredGames
  } = useFeaturedGames(5);

  // 从结果中提取游戏列表
  const gameList: GameSummary[] = gamesResult?.data?.games ?? [];
  const featuredList: GameSummary[] = featuredGames ?? [];

  if (gamesLoading && gameList.length === 0) {
    return <LoadingSpinner size="lg" text="正在加载游戏..." />;
  }

  if (gamesError) {
    return (
      <ErrorState
        title="加载失败"
        message="无法加载游戏列表，请检查网络连接"
        onRetry={() => refetchGames()}
      />
    );
  }

  // Mobile Layout
  if (isMobile) {
    const handleSearch = (query: string) => {
      navigate(`/search?q=${encodeURIComponent(query)}`);
    };

    return (
      <div className={styles.mobileContainer}>
        {/* Mobile Header with Search */}
        <header className={styles.mobileHeader}>
          <div className={styles.mobileHeaderRow}>
            <div className={styles.mobileBrand}>
              {site.logo ? (
                <img src={site.logo} alt={site.title} className={styles.mobileLogoImg} />
              ) : (
                <div className={styles.mobileLogoIcon}>
                  <Sparkles size={18} />
                </div>
              )}
              <span className={styles.mobileLogo}>{site.title}</span>
            </div>
            {features.darkMode && (
              <button className={styles.themeToggle} onClick={toggleTheme} title={theme === 'dark' ? '切换亮色模式' : '切换暗色模式'}>
                {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
              </button>
            )}
          </div>
          {features.search && (
            <div className={styles.mobileSearch}>
              <SearchBox 
                onSearch={handleSearch}
                placeholder="搜索游戏..."
                showSuggestions={true}
              />
            </div>
          )}
        </header>

        <main className={styles.mobileMain}>
          {/* Featured Carousel */}
          <MobileCarousel />
          
          {/* History Section */}
          <MobileHistory />
          
          {/* Game List */}
          <MobileGameGrid 
            games={gameList}
            title="全部游戏"
          />
        </main>

        <MobileFooter />
      </div>
    );
  }

  // Desktop Layout
  return (
    <div className={styles.desktopContainer}>
      <Sidebar activePath="/" />
      
      <div className={styles.desktopContent}>
        <DesktopHeader />
        
        <main className={styles.desktopMain}>
          {/* Featured Carousel */}
          <DesktopCarousel games={featuredList} />
          
          {/* Game Grid */}
          <DesktopGameGrid 
            games={gameList}
            title="全部游戏"
            isLoading={gamesLoading}
          />
        </main>
      </div>
    </div>
  );
};

export default HomePage;
